import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserCircle, Filter } from 'lucide-react';
import { Layout } from '../components/Layout';
import { ClientCard } from '../components/ClientCard';
import { PlanAssignmentModal } from '../components/PlanAssignmentModal';
import { ClientProfileViewModal } from '../components/ClientProfileViewModal';
import { clientApi } from '../services/api';

export function Clients() {
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'expired'>('all');
  const [assigningClientId, setAssigningClientId] = useState<string | null>(null);
  const [viewingProfileClientId, setViewingProfileClientId] = useState<string | null>(null);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await clientApi.list();
      return response.data;
    },
  });

  const filteredClients = clients.filter((client) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') {
      return client.subscription?.status === 'active';
    }
    if (filterStatus === 'expired') {
      return client.subscription?.status === 'expired' || !client.subscription;
    }
    return true;
  });

  const activeCount = clients.filter((c) => c.subscription?.status === 'active').length;
  const expiredCount = clients.filter(
    (c) => c.subscription?.status === 'expired' || !c.subscription
  ).length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="mt-2 text-gray-600">View and manage your clients here.</p>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Filter className="w-4 h-4" />
            <span>Filter:</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All ({clients.length})
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setFilterStatus('expired')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterStatus === 'expired'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Expired ({expiredCount})
            </button>
          </div>
        </div>

        {/* Client List */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading clients...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <UserCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 mb-2">
              {filterStatus === 'all'
                ? 'No clients yet'
                : `No ${filterStatus} clients`}
            </p>
            <p className="text-sm text-gray-400">
              Clients will appear here once you assign them training plans
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onAssignPlan={() => setAssigningClientId(client.id)}
                onViewProfile={() => setViewingProfileClientId(client.id)}
              />
            ))}
          </div>
        )}

        {/* Plan Assignment Modal */}
        {assigningClientId && (
          <PlanAssignmentModal
            clientId={assigningClientId}
            onClose={() => setAssigningClientId(null)}
          />
        )}

        {/* Client Profile View Modal */}
        {viewingProfileClientId && (
          <ClientProfileViewModal
            clientId={viewingProfileClientId}
            onClose={() => setViewingProfileClientId(null)}
          />
        )}
      </div>
    </Layout>
  );
}
